import { Locale, SUPPORTED_LOCALES } from './Metronici18n';

/**
 * Presentation data shared by every language control.
 *
 * There are two of them — the full card on `/profile` and the compact list in the
 * profile dropdown — and they must agree on what each language is called, which
 * flag it gets and what order they appear in. Keeping that in one module is the
 * only thing stopping the two from drifting as languages are added or reordered.
 */

/**
 * Language names are deliberately NOT translated: each option is labelled in its
 * own language ("Deutsch", "English", "Français"), which is the convention for
 * language pickers. Someone who has accidentally landed in a language they don't
 * read still needs to find their own.
 *
 * They therefore live here rather than in the catalogue — a catalogue entry would
 * be translated per locale, which is the opposite of what this needs.
 */
export const LOCALE_ENDONYMS: Record<Locale, string> = {
  de: 'Deutsch',
  en: 'English',
  fr: 'Français',
  it: 'Italiano',
};

/**
 * Flags are decoration only — the endonym is the accessible label, and the images
 * are `alt=""` + `aria-hidden`. A flag is a country, not a language (the English
 * option carries a Union Jack, which is a rough stand-in at best), so a flag on
 * its own would be a poor label for a screen reader or for anyone scanning.
 *
 * Stored as raw public paths, like the icons in `constants/navi.ts`; each consumer
 * runs them through `toAbsoluteUrl`. Artwork is Twemoji (CC-BY 4.0).
 */
export const LOCALE_FLAGS: Record<Locale, string> = {
  de: '/media/svg/flags/de.svg',
  en: '/media/svg/flags/en.svg',
  fr: '/media/svg/flags/fr.svg',
  it: '/media/svg/flags/it.svg',
};

/**
 * English sorts last, because it is the fallback nobody's market is served in —
 * no storefront is English-first, so it is the least likely choice and belongs out
 * of the way.
 */
const LAST_LOCALE: Locale = 'en';

/**
 * Display order: the active language first, then the rest in their declared
 * order, with English pushed to the end.
 *
 * The two rules can conflict — when English is active it cannot be both first and
 * last — and "active first" wins, so a customer always sees their current language
 * at the top of the list.
 */
export const orderLocales = (active: Locale): Locale[] => {
  const rest = SUPPORTED_LOCALES.filter((locale) => locale !== active);

  return [
    active,
    ...rest.filter((locale) => locale !== LAST_LOCALE),
    ...rest.filter((locale) => locale === LAST_LOCALE),
  ];
};
